import React, { useEffect, useState } from 'react';
import { Grid, ToggleButtonGroup } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { OrderPageTypography } from '../themedComponents/OrderPageTypography';
import { OrderPageToggleButton } from '../themedComponents/OrderPageToggleButton';
import { useLanguage } from '../../utils/GeneralUtils';
import { AddonUIObjectData } from '../../api/models/AddonUIObjectData';
import { customerFacingPriceFormatter } from '../utils';
import { withBookingFee } from '../core/pricing';

type AddonUiObjectProps = {
  uiObject: AddonUIObjectData;
  orderState: any;
  setOrderCustomDetails: (data: Record<string, string>) => void;
};

const AddonUiObject = ({ uiObject, setOrderCustomDetails, orderState }: AddonUiObjectProps) => {
  const { t } = useTranslation();
  const language = useLanguage();
  const defaultOption = uiObject.options[0];
  const initialOption = uiObject.options.find((o) => {
    return o.id === orderState?.details?.custom_details?.[uiObject.id];
  });
  const [value, setValue] = useState(initialOption?.id || defaultOption.id);

  const enumPrice = orderState.orderPage.price?.find((x: any) => x.name === uiObject.id);

  const getOptionPrice = (optionId: string) => {
    return enumPrice?.options?.find((x: any) => x.value === optionId)?.price;
  };

  const formatOptionPrice = (optionId: string) => {
    const price = getOptionPrice(optionId);
    if (!price) {
      return null;
    }
    const hasPerPerson = price.price_per_additional_value > 0;
    const displayPrice = hasPerPerson ? price.price_per_additional_value : price.base_price;
    if (displayPrice === 0) return null;
    const formatted = customerFacingPriceFormatter(
      withBookingFee(displayPrice, orderState.account),
      orderState.account,
    );
    return `+${formatted} ${hasPerPerson ? t('uiObjects.perPerson') : ''}`;
  };

  useEffect(() => {
    setOrderCustomDetails({ [uiObject.id]: defaultOption.id });
  }, []);

  const onValueChange = (e: React.MouseEvent<HTMLElement>, val: string | null) => {
    if (val != null) {
      setValue(val);
      setOrderCustomDetails({ [uiObject.id]: val });
    }
  };

  return (
    <Grid container direction="column">
      <Grid item>
        <OrderPageTypography variant="h3" bold>
          {uiObject.label[language]}
        </OrderPageTypography>
      </Grid>
      <Grid item sx={{ mt: 2.5, mb: 2.5, cursor: 'pointer' }}>
        <ToggleButtonGroup orientation="vertical" value={value} color="primary" exclusive onChange={onValueChange}>
          {uiObject.options.map((option) => {
            const priceText = formatOptionPrice(option.id);
            return (
              <OrderPageToggleButton
                key={option.id}
                sx={{ textTransform: 'none', cursor: 'pointer' }}
                value={option.id}>
                <Grid container alignItems="center" gap={1}>
                  {option.label[language]}
                  {priceText && (
                    <OrderPageTypography variant="body2" color="disabled" style={{ fontWeight: 300 }}>
                      {priceText}
                    </OrderPageTypography>
                  )}
                </Grid>
              </OrderPageToggleButton>
            );
          })}
        </ToggleButtonGroup>
      </Grid>
    </Grid>
  );
};

export default AddonUiObject;
